import { useState, useRef, useEffect } from 'react';

export const PROMPT_HISTORY_KEY = 'aiImage_promptHistory';
const MAX_PROMPT_HISTORY = 10;

export function savePromptToHistory(newPrompt: string) {
  try {
    const raw = localStorage.getItem(PROMPT_HISTORY_KEY);
    const existing: string[] = raw ? JSON.parse(raw) : [];
    const trimmed = newPrompt.trim();
    if (!trimmed) return;
    const deduped = [trimmed, ...existing.filter(p => p !== trimmed)].slice(0, MAX_PROMPT_HISTORY);
    localStorage.setItem(PROMPT_HISTORY_KEY, JSON.stringify(deduped));
  } catch { /* noop */ }
}

interface PromptHistoryDropdownProps {
  onSelect: (prompt: string) => void;
  darkMode: boolean;
}

export default function PromptHistoryDropdown({ onSelect, darkMode }: PromptHistoryDropdownProps) {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState<string[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const loadHistory = () => {
    try {
      const raw = localStorage.getItem(PROMPT_HISTORY_KEY);
      setHistory(raw ? JSON.parse(raw) : []);
    } catch { setHistory([]); }
  };

  useEffect(() => {
    if (open) loadHistory();
  }, [open]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const clearHistory = () => {
    try { localStorage.removeItem(PROMPT_HISTORY_KEY); } catch { /* noop */ }
    setHistory([]);
    setOpen(false);
  };

  const handleSelect = (p: string) => {
    onSelect(p);
    setOpen(false);
  };

  return (
    <div ref={dropdownRef} className="relative inline-block">
      <button
        onClick={() => setOpen(v => !v)}
        title="Prompt history"
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
          open
            ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
            : darkMode
            ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
        }`}
      >
        <i className="ri-history-line text-sm"></i>
        <span>History</span>
        {history.length > 0 && (
          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold leading-none ${
            darkMode ? 'bg-purple-900/50 text-purple-400' : 'bg-purple-100 text-purple-600'
          }`}>
            {history.length}
          </span>
        )}
        <i className={`ri-arrow-${open ? 'up' : 'down'}-s-line text-xs`}></i>
      </button>

      {open && (
        <div className={`absolute left-0 top-full mt-1.5 w-80 rounded-xl shadow-2xl z-50 overflow-hidden border ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          {/* Header */}
          <div className={`flex items-center justify-between px-4 py-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
            <span className={`text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Recent Prompts
            </span>
            {history.length > 0 && (
              <button
                onClick={clearHistory}
                className={`text-xs transition-colors ${darkMode ? 'text-red-400 hover:text-red-300' : 'text-red-500 hover:text-red-600'}`}
              >
                <i className="ri-delete-bin-line mr-1"></i>Clear all
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-72 overflow-y-auto">
            {history.length === 0 ? (
              <div className={`py-8 text-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                <i className="ri-history-line text-2xl block mb-2 opacity-40"></i>
                <p className="text-xs">No prompt history yet.</p>
                <p className="text-xs opacity-70 mt-1">Your typed prompts will appear here.</p>
              </div>
            ) : (
              history.map((p, i) => (
                <button
                  key={i}
                  onClick={() => handleSelect(p)}
                  className={`w-full text-left flex items-start gap-3 px-4 py-3 transition-colors group ${
                    darkMode
                      ? 'hover:bg-gray-700 border-b border-gray-700/50 last:border-0'
                      : 'hover:bg-purple-50 border-b border-gray-50 last:border-0'
                  }`}
                >
                  <div className={`w-5 h-5 flex-shrink-0 rounded flex items-center justify-center mt-0.5 ${
                    darkMode ? 'bg-gray-700 group-hover:bg-gray-600' : 'bg-gray-100 group-hover:bg-purple-100'
                  }`}>
                    <span className={`text-[10px] font-bold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{i + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs leading-relaxed line-clamp-2 ${darkMode ? 'text-gray-300 group-hover:text-white' : 'text-gray-700 group-hover:text-purple-700'}`}>
                      {p}
                    </p>
                    <p className={`text-[10px] mt-1 ${darkMode ? 'text-gray-600 group-hover:text-purple-400' : 'text-gray-400 group-hover:text-purple-500'}`}>
                      Click to reuse
                    </p>
                  </div>
                  <i className={`ri-arrow-right-up-line text-sm flex-shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity ${darkMode ? 'text-purple-400' : 'text-purple-500'}`}></i>
                </button>
              ))
            )}
          </div>

          {/* Footer tip */}
          {history.length > 0 && (
            <div className={`px-4 py-2.5 border-t text-[10px] flex items-center gap-1.5 ${
              darkMode ? 'border-gray-700 text-gray-600' : 'border-gray-100 text-gray-400'
            }`}>
              <i className="ri-information-line"></i>
              Saves your last {MAX_PROMPT_HISTORY} unique prompts automatically
            </div>
          )}
        </div>
      )}
    </div>
  );
}
